// nuolino-admin/src/components/layout/AppShell.tsx
// Shell autenticado: sidebar desktop (lg+) + drawer mobile (<md) + header + content.

import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Menu, Search, LogOut } from 'lucide-react';
import { cn } from '@/lib/cn';
import { Button } from '@/components/ui';
import { useAuth } from '@/auth';
import { Sidebar } from './Sidebar';
import { MobileDrawer } from './MobileDrawer';
import { CommandPalette } from './CommandPalette';
import { SseNotificationBell } from './SseNotificationBell';

export interface AppShellProps {
  children: ReactNode;
  className?: string;
}

function AppHeader({ onOpenCommandPalette }: { onOpenCommandPalette: () => void }) {
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const [drawerOpen, setDrawerOpen] = useState(false);

  return (
    <>
      <header className="sticky top-0 z-sticky flex h-16 items-center justify-between gap-3 border-b border-border-subtle bg-surface px-4 sm:px-6">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            className="lg:hidden"
            onClick={() => setDrawerOpen(true)}
            aria-label={t('common.open_menu')}
            leftIcon={<Menu className="h-5 w-5" />}
          >
            <span className="sr-only">{t('common.menu')}</span>
          </Button>
          <button
            type="button"
            onClick={onOpenCommandPalette}
            className="hidden items-center gap-2 rounded-md border border-border bg-surface-alt px-3 py-1.5 text-sm text-text-muted transition-colors hover:bg-surface sm:flex sm:w-72"
            aria-label="Abrir command palette (Ctrl+K)"
          >
            <Search className="h-4 w-4" aria-hidden="true" />
            <span className="flex-1 text-left">{t('common.search_placeholder')}</span>
            <kbd className="rounded border border-border-subtle bg-surface px-1.5 py-0.5 text-xs text-text-muted">⌘K</kbd>
          </button>
        </div>
        <div className="flex items-center gap-2">
          <SseNotificationBell />
          <span className="hidden text-sm text-text-muted sm:inline">{user?.name}</span>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => void logout()}
            aria-label={t('common.logout')}
            leftIcon={<LogOut className="h-4 w-4" />}
          >
            <span className="hidden sm:inline">{t('common.logout')}</span>
          </Button>
        </div>
      </header>
      <MobileDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} title={t('app.name')}>
        <Sidebar onNavigate={() => setDrawerOpen(false)} />
      </MobileDrawer>
    </>
  );
}

export function AppShell({ children, className }: AppShellProps) {
  const [paletteOpen, setPaletteOpen] = useState(false);
  return (
    <div className={cn('flex min-h-screen bg-bg text-text', className)}>
      <div className="hidden lg:block">
        <Sidebar />
      </div>
      <div className="flex min-h-screen flex-1 flex-col">
        <AppHeader onOpenCommandPalette={() => setPaletteOpen(true)} />
        <main className="mx-auto w-full max-w-content flex-1 px-4 py-6 sm:px-6 lg:px-8">{children}</main>
      </div>
      {paletteOpen && <CommandPalette onClose={() => setPaletteOpen(false)} />}
    </div>
  );
}
